import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import * as LucideIcons from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Database } from "@/integrations/supabase/types";
import { useLanguage } from "@/hooks/useLanguage";
import { logger } from "@/lib/logger";

type BadgeProgress = Database['public']['Tables']['user_badges_progress']['Row'];
type BadgeFromDB = Database['public']['Tables']['badges']['Row'];

type Badge = {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  category: string;
  progress_steps: number;
  progress: number;
  unlocked: boolean;
  earned_at: string | null;
  credit_reward: number | null;  // Added credit_reward field
  unlock_condition: {
    type: string;
    count?: number;
    genre?: string;
    rating?: number;
  };
};

export const BadgesTab = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  // Fetch all badges and user's progress
  const { data: badgesData, isLoading } = useQuery({
    queryKey: ["badges-progress"],
    queryFn: async () => {
      // Fetch all badges
      const { data: badges, error: badgesError } = await supabase
        .from("badges")
        .select("*")
        .order("category", { ascending: true });

      if (badgesError) {
        toast({
          title: t("dashboard.stats.badges.error.title"),
          description: t("dashboard.stats.badges.error.loadBadges"),
          variant: "destructive",
        });
        throw badgesError;
      }

      // Fetch user's progress
      const { data: progress, error: progressError } = await supabase
        .from("user_badges_progress")
        .select("*");

      if (progressError) {
        toast({
          title: t("dashboard.stats.badges.error.title"),
          description: t("dashboard.stats.badges.error.loadProgress"),
          variant: "destructive",
        });
        throw progressError;
      }

      // Create a map of badge progress
      const progressMap = new Map(
        progress?.map((p: BadgeProgress) => [p.badge_id, p]) || []
      );

      // Combine badges with their progress and parse unlock_condition
      return (badges || []).map((badge: BadgeFromDB) => {
        const unlockCondition = typeof badge.unlock_condition === 'string' 
          ? JSON.parse(badge.unlock_condition) 
          : badge.unlock_condition;

        const userProgress = progressMap.get(badge.id);
        
        // Badge is unlocked if there's progress data with unlocked=true
        const isUnlocked = !!userProgress?.unlocked;
        
        return {
          ...badge,
          unlock_condition: unlockCondition,
          progress: userProgress?.progress || 0,
          unlocked: isUnlocked,
          earned_at: userProgress?.earned_at || null,
        } as Badge;
      });
    },
  });

  if (isLoading) {
    return <div className="text-white/60">{t("dashboard.stats.badges.loading")}</div>;
  }

  // Group badges by category
  const groupedBadges = badgesData?.reduce((acc: { [key: string]: { categoryId: string, badges: Badge[] } }, badge: Badge) => {
    if (!acc[badge.category]) {
      // Store the first badge ID for each category to use for translation lookup
      acc[badge.category] = {
        categoryId: badge.id,
        badges: []
      };
    }
    acc[badge.category].badges.push(badge);
    return acc;
  }, {});

  const renderBadgeIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    if (!Icon) {
      logger.warn(`Icon not found: ${iconName}`);
      return null;
    }
    return <Icon className="w-8 h-8" />;
  };

  const formatProgressValue = (value: number, total: number): string => {
    if (total >= 1000) {
      return `${(value / 1000).toFixed(1)}k/${(total / 1000).toFixed(1)}k`;
    }
    return `${value}/${total}`;
  };

  return (
    <div className="space-y-8">
      {Object.entries(groupedBadges || {}).map(([category, { categoryId, badges }]) => {
        // Use the first badge from each category to get its translated category
        const firstBadge = badges[0];
        
        // Try to get the translated category from the badge translations
        const translatedCategory = t(`dashboard.stats.badges.items.${categoryId}.category`);
        
        return (
          <div key={category}>
            <h2 className="text-xl font-semibold text-white mb-4">{translatedCategory}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map((badge: Badge) => (
                <Card
                  key={badge.id}
                  className={cn(
                    "p-4 transition-all duration-200",
                    badge.unlocked
                      ? "bg-white/10 border-white/20"
                      : "bg-white/5 border-white/10"
                  )}
                >
                  <div className="flex items-start space-x-4">
                    <div className={cn(
                      "p-2 rounded-lg",
                      badge.unlocked ? "text-yellow-400" : "text-gray-400"
                    )}>
                      {renderBadgeIcon(badge.icon_name)}
                      {badge.credit_reward && (
                        <div className={cn(
                          "flex items-center mt-3",
                          badge.unlocked ? "text-yellow-400" : "text-gray-400"
                        )}>
                          <LucideIcons.Coins className="w-4 h-4 mr-1" />
                          <span className="text-xs">{badge.credit_reward}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white">
                          {t(`dashboard.stats.badges.items.${badge.id}.name`) || badge.name}
                        </h3>
                        {!badge.unlocked && badge.progress_steps > 1 && (
                          <span className="text-sm text-gray-400">
                            {formatProgressValue(badge.progress, badge.progress_steps)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-300 mt-1">
                        {t(`dashboard.stats.badges.items.${badge.id}.description`) || badge.description}
                      </p>
                      {badge.progress_steps > 1 && (
                        <Progress
                          value={(badge.progress / badge.progress_steps) * 100}
                          className="h-1 mt-2 bg-white/10"
                        />
                      )}
                      {badge.earned_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          {t("dashboard.stats.badges.earned")} {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
